import { useState, useMemo, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Icon, Popup } from 'semantic-ui-react';
import './VTOBottomPanel.css';

import OriginalImageSection from './components/OriginalImageSection';
import CurrentlyWearingSection from './components/CurrentlyWearingSection';
import FitHistorySection from './components/FitHistorySection';
import ProcessingModal from '../common/ProcessingModal';
import InviteFriendsButton from '../common/InviteFriendsButton';
import CreditPurchaseModal from '../common/CreditPurchaseModal';
import { useSearchParams } from 'react-router-dom';
import { BACKEND_URL } from '../../utils/config';
import ShareModal from './components/ShareModal';
import LicensePurchaseModal from '../licensing/LicensePurchaseModal';
import { useCheckLicenseQuery } from '../../redux/apis/endpoints/licensingApi';


import { selectHasVTOResult, selectIsVTOProcessing } from '../../redux/slices/vtoSlice';
import { selectActiveItems } from '../../redux/slices/clothingItemsSlice';
import { useTryOnMode } from '../../hooks/useTryOnMode';
import { useVTOBottomPanel } from './hooks/useVTOBottomPanel';
import { useCredits } from '../../hooks/useCredits';
import { createCanvasBlob } from '../../utils/imageDownload';
import { useVTOResults } from '../../hooks/useVTOResults';

const VTOBottomPanel = ({ virtualTryOnResults, onPersonImageUploadModalOpen, droppedItems = [], isVirtualMode: isVirtualModeProp, hasPersonImage = false, canvasRef, panelHeight = 250, panelPosition = 'bottom', isCollapsed: isPanelCollapsed = false, externalCollapsed, onExternalCollapseChange, onRemoveItem, disabled = false, canvasWidth, canvasHeight, background, activePersonImage, onOpenLookbookPanel, onOpenClothingPanel, onScrollToItem, hasClothingItems = true }) => {
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const [showBgRemovalProcessing, setShowBgRemovalProcessing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isCreditModalOpen, setIsCreditModalOpen] = useState(false);
  const [isLookbookModalOpen, setIsLookbookModalOpen] = useState(false);
  const [isLicenseModalOpen, setIsLicenseModalOpen] = useState(false);
  const [, setSearchParams] = useSearchParams();
  const [subscription, setSubscription] = useState(null);

  // Load subscription status so the credits popup can offer "Manage subscription"
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/credit-packages/subscription-status`, {
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (res.ok && alive) setSubscription(await res.json());
      } catch (e) { /* non-fatal */ }
    })();
    return () => { alive = false; };
  }, []);

  const openManageSubscription = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('manage', 'subscription');
      return next;
    });
  };

  const { isVirtualMode: isVirtualModeHook } = useTryOnMode();
  const { balance, hasCredits, daysUntilReset } = useCredits();
  const { togglePublishVTOResult, isPublishing } = useVTOResults();

  // Refs to communicate with child components
  const originalImageSectionRef = useRef(null);

  // Use external collapsed state if provided, otherwise use internal
  const isCollapsed = externalCollapsed !== undefined ? externalCollapsed : internalCollapsed;
  const setIsCollapsed = onExternalCollapseChange || setInternalCollapsed;

  // Use prop if provided, otherwise fall back to hook
  const isVirtualMode = isVirtualModeProp !== undefined ? isVirtualModeProp : isVirtualModeHook;
  const hasVTOResult = useSelector(selectHasVTOResult);
  const isGenerating = useSelector(selectIsVTOProcessing);
  const activeItems = useSelector(selectActiveItems);

  // Auto-collapse when no person image
  useEffect(() => {
    if (!hasPersonImage) {
      setIsCollapsed(true);
    }
  }, [hasPersonImage]);

  // Auto-expand when VTO result is generated
  useEffect(() => {
    if (hasVTOResult && isVirtualMode) {
      setIsCollapsed(false);
    }
  }, [hasVTOResult, isVirtualMode]);

  const paidItem = useMemo(() => {
    return activeItems.find(item => item.licensingEnabled && item.licensingType === 'paid');
  }, [activeItems]);

  const paidItemSourceId = paidItem?.source_product?.id || paidItem?.source_listing?.id || paidItem?.id;

  const { data: licenseCheck, refetch: refetchLicense } = useCheckLicenseQuery(
    { listingId: paidItemSourceId, itemType: 'product' },
    { skip: !paidItem }
  );

  const hasLicense = licenseCheck?.hasLicense || false;
  const needsLicense = paidItem && !hasLicense && hasVTOResult;

  console.log('[VTOBottomPanel] License Check:', {
    paidItem: paidItem ? {
      id: paidItem.id,
      licensingEnabled: paidItem.licensingEnabled,
      source_product: paidItem.source_product,
      source_listing: paidItem.source_listing
    } : null,
    hasLicense,
    needsLicense,
    hasVTOResult
  });

  const isPaidLicense = paidItem?.licensingEnabled && paidItem?.licensingType === 'paid';
  const currency = paidItem?.currency || 'USD';
  const formattedPrice = isPaidLicense && paidItem?.licensingPrice
      ? new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 0 }).format(paidItem.licensingPrice)
      : null;

  const {
    currentGeneration,
    handleDownload,
    handleUpdatePersonImage
  } = useVTOBottomPanel(droppedItems);

  // Download handler with mobile support (saves to album via Web Share API)
  const handleDownloadClick = async () => {
    if (isDownloading) return;

    setIsDownloading(true);
    try {
      // Get the canvas element for composite download
      const canvasElement = canvasRef?.current || document.querySelector('.main-canvas');

      // Check if we have a VTO result image URL
      const imageUrl = currentGeneration?.imageUrl;

      // For mobile devices, try Web Share API first (allows saving to album)
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

      if (isMobile && navigator.share && navigator.canShare) {
        try {
          // Create blob from canvas or fetch image
          let blob;
          const filename = `susu-closet-look-${Date.now()}.jpg`;

          if (activeItems.length > 0 && canvasElement) {
            // Use createCanvasBlob which properly uses natural dimensions to preserve aspect ratio
            blob = await createCanvasBlob(canvasElement);
          } else if (imageUrl) {
            const response = await fetch(imageUrl);
            blob = await response.blob();
          }

          if (blob) {
            const file = new File([blob], filename, { type: 'image/jpeg' });

            if (navigator.canShare({ files: [file] })) {
              await navigator.share({
                files: [file],
                title: 'My SUSU Closet Look'
              });
              console.log('✅ Image shared/saved via Web Share API');
              return;
            }
          }
        } catch (shareError) {
          console.log('📱 Web Share not available, falling back to download:', shareError);
        }
      }

      // Fallback: standard download
      await handleDownload(imageUrl, canvasElement);
      console.log('✅ Image downloaded');
    } catch (error) {
      console.error('❌ Download failed:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  // Also check existing VTO results as fallback
  const hasExistingVTOResults = virtualTryOnResults && virtualTryOnResults.length > 0;



  const shouldShowPanel = useMemo(() => {
    return isVirtualMode;
  }, [isVirtualMode]);

  if (!shouldShowPanel) {
    return null;
  }

  // Calculate bottom position based on panel state
  // Use 28px gap to match top and left edge spacing
  // When clothing panel is collapsed, add 32px for the collapsed bar height + 28px gap
  const bottomPosition = panelPosition === 'bottom' && !isPanelCollapsed
    ? panelHeight + 28
    : 32 + 28; // collapsed bar height (32px) + gap (28px)

  // Calculate left position to center in canvas area (not whole window)
  // If canvasWidth is provided, center within that width, otherwise center in window
  // Cap max-width at 800px for better readability
  const panelStyle = canvasWidth ? {
    bottom: `${bottomPosition}px`,
    left: `${canvasWidth / 2}px`,
    transform: 'translateX(-50%)',
    maxWidth: `${Math.min(800, canvasWidth - 40)}px` // 20px padding on each side, max 800px
  } : {
    bottom: `${bottomPosition}px`,
    left: '50%',
    transform: 'translateX(-50%)'
  };

  if (isCollapsed) {
    const collapsedStyle = {
      bottom: `${bottomPosition}px`
    };

    return (
      <div className="vto-floating-panel collapsed" style={collapsedStyle}>
        <Popup
          trigger={
            <button
              className="expand-vto-btn"
              onClick={() => setIsCollapsed(false)}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 2L16.3 8.7L23 11L16.3 13.3L14 20L11.7 13.3L5 11L11.7 8.7L14 2Z" />
                <path d="M5.5 1L6.6 4.4L10 5.5L6.6 6.6L5.5 10L4.4 6.6L1 5.5L4.4 4.4L5.5 1Z" opacity="0.7" />
                <path d="M6 17L7.1 19.9L10 21L7.1 22.1L6 25L4.9 22.1L2 21L4.9 19.9L6 17Z" opacity="0.5" />
              </svg>
            </button>
          }
          content="Show VTO Panel"
          position="top center"
          size="mini"
          inverted
        />
      </div>
    );
  }

  return (
    <div className="vto-floating-panel" style={panelStyle}>
      <div className="vto-panel-container">
        {/* Collapse button on lower left border */}
        <button
          className="collapse-vto-btn-border"
          onClick={() => setIsCollapsed(true)}
        >
          <Icon name="minus" />
        </button>
        {/* Header Row with Action Buttons */}
        <div className="vto-panel-header">
          <div className="vto-header-spacer">
            <Popup
              trigger={
                <span
                  className={`vto-credits-display ${!hasCredits ? 'no-credits' : ''}`}
                  onClick={() => !hasCredits && setIsCreditModalOpen(true)}
                  style={{ cursor: !hasCredits ? 'pointer' : 'default' }}
                >
                  <Icon name="bolt" /> Credits: {balance ?? '--'}
                </span>
              }
              content={
                <div style={{ textAlign: 'center', padding: '5px' }}>
                  <p style={{ marginBottom: '4px', fontWeight: 'bold' }}>
                    {balance ?? '--'} credits remaining
                  </p>
                  {/* <p style={{ marginBottom: '8px', fontSize: '0.85em', color: '#666' }}>
                    1 credit per AI generation
                  </p> */}
                  <p style={{ marginBottom: '10px', fontSize: '0.85em' }}>
                    {daysUntilReset == null ? 'Buy credits or invite friends to earn more.' : `Subscription credits renew in ${daysUntilReset} days.`}
                  </p>
                    <button
                      onClick={() => setIsCreditModalOpen(true)}
                      style={{
                        width: '100%',
                        background: '#00b5ad',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 6,
                        padding: '8px 14px',
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        marginBottom: 8
                      }}
                    >
                      Get Credits
                    </button>
                  {subscription && subscription.active && (
                    <button
                      onClick={openManageSubscription}
                      style={{
                        width: '100%',
                        background: 'transparent',
                        color: '#374151',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        padding: '8px 14px',
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        marginBottom: 8
                      }}
                    >
                      Manage subscription
                    </button>
                  )}
                  <InviteFriendsButton size="tiny" fluid />
                </div>
              }
              position="top center"
              size="small"
              hoverable
              on={['hover', 'click']}
            />
          </div>
          <div className="vto-header-actions">
            {/* Share Button - opens modal with download + copy share link options */}
            <button
              className="vto-header-btn share-btn"
              onClick={() => setIsShareModalOpen(true)}
              disabled={!currentGeneration && activeItems.length === 0}
              title="Share this look"
            >
              <Icon name="share alternate" />
              {' Share'}
            </button>
          </div>
        </div>

        {/* Paid License Upgrade Card */}
        {needsLicense && (
          <div style={{ width: '100%', marginBottom: '12px', padding: '0 16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ padding: '10px 14px', background: 'linear-gradient(180deg, #F5FBF8 0%, #E1F5EE 100%)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: 500, color: '#04342C', marginBottom: '2px', textAlign: 'left' }}>Get the clean, full-resolution image</div>
                    <div style={{ fontSize: '12px', color: '#0F6E56', lineHeight: 1.4, textAlign: 'left' }}>License this design -- no watermark, share or print anywhere.</div>
                </div>
                <button
                    onClick={() => setIsLicenseModalOpen(true)}
                    style={{ background: '#1D9E75', border: 'none', color: 'white', fontWeight: 500, padding: '8px 14px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}
                >
                    License {formattedPrice}
                </button>
            </div>
            <div style={{ fontFamily: 'serif', fontSize: '11px', fontStyle: 'italic', color: '#9ca3af', lineHeight: 1.4, textAlign: 'center' }}>
                Preview is for personal viewing. The watermarked image may not be removed or used commercially.
            </div>
          </div>
        )}

        {/* Content Row with Sections */}
        <div className="vto-panel-content">
          <OriginalImageSection
            ref={originalImageSectionRef}
            onUpdateImage={onPersonImageUploadModalOpen || handleUpdatePersonImage}
            onBgRemovalStart={() => setShowBgRemovalProcessing(true)}
            onBgRemovalEnd={() => setShowBgRemovalProcessing(false)}
          />
          <CurrentlyWearingSection
            onRemoveItem={onRemoveItem}
            disabled={disabled}
            onOpenClothingPanel={onOpenClothingPanel}
            onScrollToItem={onScrollToItem}
            panelPosition={panelPosition}
            hasClothingItems={hasClothingItems}
          />
          <FitHistorySection onOpenLookbookPanel={onOpenLookbookPanel} />
        </div>
      </div>

      {/* Background Removal Processing Modal */}
      <ProcessingModal
        isOpen={showBgRemovalProcessing}
        title="Background Removal"
        message="Removing background from your image..."
        icon="image"
        creditBalance={balance}
        creditsUsed={1}
        tip="Creating a clean cutout..."
      />

      {/* Share Modal */}
      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        onDownload={handleDownloadClick}
      />

      {/* Credit Purchase Modal */}
      <CreditPurchaseModal
        isOpen={isCreditModalOpen}
        onClose={() => setIsCreditModalOpen(false)}
      />

      {/* License Purchase Modal */}
      {paidItem && (
        <LicensePurchaseModal
          isOpen={isLicenseModalOpen}
          onClose={() => setIsLicenseModalOpen(false)}
          listing={paidItem.source_product || paidItem.source_listing || paidItem}
          onSuccess={() => {
            refetchLicense();
          }}
        />
      )}

    </div>
  );
};

export default VTOBottomPanel;